/**
 * @file A representation of an interactive box.
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2404.03.001
 */

/** @module interactive */

import { Box } from "./Box.js";

/** 
 * A representation of an interactive box. 
 * @class
 * @augments module:box~Box
 */
class Interactive extends Box {
	static defaultStyleProperties = Object.freeze( {
		font: "24px 'Retro Gaming', monospace",
		textAlign: "left",
		textBaseline: "top",
		textColour: "#fff",
		backgroundColour: "rgba(0, 0, 0, 0.666)",
		borderColour: "#fff"
	} )
	name = "";
	type = "";
	text = "";
	styleProperties = Interactive.defaultStyleProperties;
	goToProperties = {};
	hintPosition = {};
	element = null;
	options = [];
	selectedOption = -1;
	/**
	 * Create an Interactive box.
	 * Origin of coordinates is at the top left, with x increasing to the right
	 * and y increasing downwards.
	 * @param {Number}       [x=0]      
	 * @param {Number}       [y=0]
	 * @param {Number}       [width=0]         Cannot be a negative number.
	 * @param {Number}       [height=0]        Cannot be a negative number.
	 * @param {String}       name              A label to identify the interactive.
	 * @param {String}       type              A type label.
	 * @param {String}       text              A text. Use "\n" for line-breaks.
	 * @param {Object}       [styleProperties] See Interactive.defaultStyleProperties
	 * @param {Object}       [goToProperties]  Defines a screen/position change.
	 * @example <caption>Box in a box</caption>
	 * const interactive = new Interactive( 
	 *   256, 512, 400, 100, 
	 *   "interactive-01", "navigation", 
	 *   "This is\na multi-line\ntext."
	 * );
	 */
	constructor( 
		x, y, width, height, 
		name, type, text, 
		styleProperties = {},
		goToProperties = {},
		hintPosition = {}
	) {
		super( x, y, width, height );
		this.name = name;
		this.type = type;
		this.text = text;
		this.styleProperties = Object.assign( 
			{}, Interactive.defaultStyleProperties, styleProperties
		);
		this.goToProperties = goToProperties;
		this.hintPosition = ( Object.keys( hintPosition ).length > 0 )
			? hintPosition
			: { x: x + width / 2, y: y }
		;
	}
}

export { Interactive }